import BootstrapTable from 'react-bootstrap-table-next';
import { PaginationFooter } from '../../Table/PaginationFooter';
import {
  disabledCheckboxFormatter,
  manageMedicationDeleteFormatter,
} from '../helper';
import { MedicationProfileSettingsFilter } from './MedicationProfileSettingsFilter';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MedForm } from '../MedForm';
import editPenOutlineIcon from '../../../../assets/img/button-icons/edit-pen-outline.svg';
import plusCircleIcon from '../../../../assets/img/button-icons/plus-circle-white.svg';
import editPenSolidIcon from '../../../../assets/img/button-icons/edit-pen-solid.svg';
import { usePagination } from '../../Table/PaginationFooter/hooks/usePagination';
import { useForm } from 'react-hook-form';
import { useAddCatalogMed } from '../MedForm/hooks';
import { useSelector } from 'react-redux';
import { SystemPermission, useGlobalLoading } from '../../../../context';
import { useMedicationMedCatalogue } from './context/MedicationMedCatalogueProvider';
import CaremapprConfirmation from '../../Confirmation/CaremapprConfirmation';

const columns = [
  {
    dataField: 'med_name',
    text: 'Med name',
    classes: 'col6',
    headerClasses: 'user-normal-header',
  },
  {
    dataField: 'brand',
    text: 'Brand name',
    classes: 'col6',
    headerClasses: 'user-normal-header',
  },
  {
    dataField: 'subheading',
    text: 'Other important information',
    classes: 'col12',
    headerClasses: 'user-normal-header',
  },
  {
    dataField: 'indication',
    text: 'Indication',
    classes: 'col12',
    headerClasses: 'user-normal-header',
  },
  {
    dataField: 'details.regimen.isDoseSchedule',
    text: 'Dosing schedule',
    classes: 'col6',
    formatter: disabledCheckboxFormatter,
    headerClasses: 'user-normal-header text-center',
  },
  {
    dataField: 'details.regimen.isPrn',
    text: 'PRN',
    classes: 'col6',
    formatter: disabledCheckboxFormatter,
    headerClasses: 'user-normal-header text-center',
  },
  {
    dataField: 'details.regimen.isTaper',
    text: 'Taper',
    classes: 'col6',
    formatter: disabledCheckboxFormatter,
    headerClasses: 'user-normal-header text-center',
  },
];

const expandRow = {
  className: 'expanding-foo',
  renderer: (row) => (
    <div>
      <MedForm
        inlineFlex={true}
        data={row}
        onCancelEdit={() => {
          document.getElementById(`catalogue-${row.id}`).click();
        }}
      />
    </div>
  ),
  showExpandColumn: true,
  expandByColumnOnly: true,
  // only support 'left' or 'right'
  expandColumnPosition: 'right',
  expandHeaderColumnRenderer: ({ isAnyExpands }) => {
    return null;
  },
  expandColumnRenderer: ({ expanded, rowKey }) => {
    return (
      <>
        <button id={`catalogue-${rowKey}`}>
          {expanded ? (
            <img src={editPenSolidIcon} className="icon" alt="edit pen icon" />
          ) : (
            <img
              src={editPenOutlineIcon}
              className="icon"
              alt="edit pen icon"
            />
          )}
        </button>
      </>
    );
  },
};

const medicationMapper = (data) => {
  return data.map((item) => {
    return {
      ...item,
      indication: Array.isArray(item?.details?.indications)
        ? item?.details?.indications.map((item) => item.indication).join(', ')
        : '',
    };
  });
};

export const MedicationMedCatalogue = () => {
  const [forceRender, setForceRender] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const activeId = useRef(null);
  const skipInitialPageFetchRef = useRef(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showNewMedForm, setShowNewMedForm] = useState(false);
  const {
    meds,
    isLoading: isLoadingMeds,
    setFilters,
    setPage,
    setPageSize: setCataloguePageSize,
    totalPages: catalogueTotalPages,
    applyFiltersAndFetch,
    fetchIfNeeded,
  } = useMedicationMedCatalogue();
  const { postAddCatalogMed, isLoading } = useAddCatalogMed();
  const { selectedTenancy } = useSelector((state) => state.clinicianProfile);
  const { startLoading, stopLoading } = useGlobalLoading();

  const canEditMedicationCatalog =
    selectedTenancy?.userPermissions?.[
      SystemPermission.MANAGE_MEDICINE_CATALOG
    ] || false;

  const medFilterForm = useForm({
    defaultValues: {
      medName: '',
      indication: '',
      therapeuticArea: '',
      therapeuticClass: '',
      isDoseSchedule: undefined,
      isPrn: undefined,
      isTaper: undefined,
    },
  });

  const columnsWithAction = useMemo(() => {
    if (canEditMedicationCatalog) {
      return [
        ...columns,
        {
          dataField: 'id',
          text: '',
          classes: 'col3 text-center',
          formatter: (cell, row) =>
            manageMedicationDeleteFormatter(
              cell,
              row,
              () => {
                setShowConfirmModal(true);
              },
              isLoading || isLoadingMeds,
            ),
          headerClasses: 'user-normal-header',
        },
      ];
    }

    return columns;
  }, [canEditMedicationCatalog]);

  const {
    currentPage,
    pageSize,
    canPreviousPage,
    handleNextPage,
    handlePreviousPage,
    setPageSize,
    resetCurrentPage,
    canNextPage,
  } = usePagination({
    defaultSize: 10,
    totalPages: catalogueTotalPages > 0 ? catalogueTotalPages : undefined,
  });
  const sort = { dataField: 'id', order: 'asc' };

  const rowEvents = {
    onClick: (e, row, rowIndex) => {
      if (e.target && e.target.getAttribute('id')) {
        setActiveIndex(rowIndex);
        setShowConfirmModal(true);
        activeId.current = row.id;
      }
    },
  };

  const mappedData = medicationMapper(meds);
  const resolvedCanNextPage =
    catalogueTotalPages > 0 ? canNextPage : meds.length >= pageSize;

  const handleFilterChange = () => {
    setForceRender((count) => {
      return count + 1;
    });
  };

  const onDeleteMedication = async (activeIndex) => {
    const itemToDelete = meds[activeIndex];
    const payload = {
      medId: itemToDelete.id,
      isDeleted: true,
    };
    setShowConfirmModal(false);
    await postAddCatalogMed(payload);
    setForceRender((count) => {
      return count + 1;
    });
  };

  useEffect(() => {
    if (isLoadingMeds || isLoading) {
      startLoading();
    } else {
      stopLoading();
    }
  }, [isLoadingMeds || isLoading]);

  useEffect(() => {
    const filterValues = medFilterForm.getValues();
    setPage(currentPage);
    setCataloguePageSize(pageSize);
    applyFiltersAndFetch(filterValues, {
      force: true,
      page: currentPage,
      pageSize,
    }).then(() => {
      resetCurrentPage(1);
    });
  }, [forceRender]);

  useEffect(() => {
    const filterValues = medFilterForm.getValues();
    setPage(currentPage);
    setCataloguePageSize(pageSize);
    if (skipInitialPageFetchRef.current) {
      skipInitialPageFetchRef.current = false;
      return;
    }
    applyFiltersAndFetch(filterValues, {
      force: true,
      page: currentPage,
      pageSize,
    });
  }, [currentPage, pageSize]);

  return (
    <div className="medication-med-catalogue-container">
      <MedicationProfileSettingsFilter
        form={medFilterForm}
        handleFilterChange={handleFilterChange}
      />
      <div className="table-actions">
        {canEditMedicationCatalog ? (
          <button
            className="btn btnPrimary"
            style={{ minWidth: 110 }}
            type="button"
            onClick={() => setShowNewMedForm(true)}
          >
            Add new med{' '}
            <img src={plusCircleIcon} alt="add new medication icon" />
          </button>
        ) : null}
      </div>
      {showNewMedForm ? (
        <MedForm
          onCancelEdit={() => {
            setShowNewMedForm(false);
            setForceRender((count) => count + 1);
          }}
        />
      ) : null}
      <div style={{ position: 'relative' }}>
        <BootstrapTable
          bootstrap4
          keyField="id"
          data={mappedData}
          columns={columnsWithAction}
          bordered={false}
          classes="user-table"
          sort={sort}
          rowEvents={rowEvents}
          expandRow={canEditMedicationCatalog ? expandRow : []}
        />
        <PaginationFooter
          disabled={isLoadingMeds || isLoading}
          currentPage={currentPage}
          showTotalPage={false}
          canNextPage={resolvedCanNextPage}
          canPreviousPage={canPreviousPage}
          handleNextPage={handleNextPage}
          handlePreviousPage={handlePreviousPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
        <CaremapprConfirmation
          open={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          text="Are you sure to delete this medication?"
          onConfirm={() => onDeleteMedication(activeIndex)}
        />
      </div>
    </div>
  );
};
